'use client';

import {useSidebarStore} from './sidebarStore';
import SidebarNav from './SidebarNav';
import CollapsedSidebar from './CollapsedSidebar';
import Header from '../Header';
import {
  COLLAPSED_SIDEBAR_WIDTH_PX,
  EXPANDED_SIDEBAR_WIDTH_PX,
} from './sidebar-layout-constants';
import {
  useQuotesFirst,
  useQuotingAvailability,
} from '@/matic/features/quoting/hooks/useQuotingAvailability';
import {QuotingSidebar} from './QuotingSidebar';

type SidebarLayoutProps = {
  children: React.ReactNode;
  /**
   * When true, omits the top Header bar and uses overflow-hidden on the main
   * content area. Use for AI workspace routes (Scout, Lattice) that need the
   * full viewport height for embedded document viewers.
   * Navigation is fully covered by the sidebar in this mode.
   */
  hideHeader?: boolean;
};

export default function SidebarLayout({
  children,
  hideHeader = false,
}: SidebarLayoutProps) {
  const isOpen = useSidebarStore((state) => state.isOpen);

  const {loading, enabled: quotingEnabled} = useQuotingAvailability();
  const quotesFirst = useQuotesFirst();

  // Which shell this tenant gets isn't known until its capabilities are: draw neither yet.
  if (quotesFirst === undefined)
    return <div className="h-screen w-full bg-neutral-50" />;

  if (quotesFirst) {
    // The AES shell has no rail: Home, Quotes, Resources and the person live in
    // each page's ribbon. Temporary: AES keeps its own shell and desk layout until we
    // consolidate it onto the QuotingSidebar and QuoteWorkbench the other quoting tenants use.
    return (
      <div className="flex h-screen w-full flex-col">
        {!hideHeader && <Header />}
        <main
          className={`min-h-0 flex-1 bg-neutral-50 ${hideHeader ? 'overflow-hidden' : 'overflow-auto'}`}
        >
          {children}
        </main>
      </div>
    );
  }

  const sidebarWidth = isOpen
    ? EXPANDED_SIDEBAR_WIDTH_PX
    : COLLAPSED_SIDEBAR_WIDTH_PX;

  return (
    <div className="flex h-screen w-full flex-row">
      <div
        className={`flex flex-col overflow-hidden border-r border-sidebar-border bg-sidebar-bg shadow-md transition-all duration-300 ${
          isOpen ? 'w-64' : 'w-16'
        }`}
        style={{
          minWidth: sidebarWidth,
          maxWidth: sidebarWidth,
        }}
      >
        {loading ? null : quotingEnabled ? (
          <QuotingSidebar collapsed={!isOpen} />
        ) : isOpen ? (
          <SidebarNav />
        ) : (
          <CollapsedSidebar />
        )}
      </div>
      <div className="relative flex min-w-0 flex-1 flex-col transition-all duration-300">
        {!hideHeader && <Header />}
        <main
          className={`flex-1 bg-neutral-50 ${hideHeader ? 'overflow-hidden' : 'overflow-auto'}`}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
